import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  Heart,
  Home,
  Info,
  ShieldPlus,
  Stethoscope,
} from 'lucide-react';

import { getSignedUrl } from '../../api/pets';
import { NoPetsEmptyState } from '@/pet/src/components/NoPetsEmptyState';
import { ImageWithFallback } from '@/pet/src/components/figma/ImageWithFallback';
import { Badge } from '@/pet/src/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/pet/src/components/ui/card';
import { Progress } from '@/pet/src/components/ui/progress';
import { usePets } from '@/pet/src/context/PetsContext';
import { getPetTypeFallbackUrl } from '@/pet/src/features/know-your-pet/breed-images';
import {
  findYourPetBreeds,
  findYourPetPetTypes,
  getFindYourPetImageUrl,
  type FindYourPetBreed,
} from '@/pet/src/features/know-your-pet/find-your-pet-dataset';
import {
  getFindYourPetTranslation,
  type Language as FindYourPetLanguage,
} from '@/pet/src/features/know-your-pet/translations';
import { DashboardHeaderControls } from '@/pet/src/components/DashboardHeaderControls';

const NL_BREED_DESCRIPTIONS: Record<string, string> = {
  beagle:
    'Vriendelijk en nieuwsgierig. Beagles zijn kleine tot middelgrote jachthonden met een uitstekend reukvermogen en een vriendelijk karakter.',
  labradoodle:
    'Energiek en vriendelijk. Labradoodles werden oorspronkelijk gefokt als hypoallergene assistentiehonden.',
  'bernese-mountain-dog':
    'Zacht en kalm. Berner Sennenhonden zijn grote, driekleurige werkende honden met een zachtaardig temperament.',
};

function slugForValue(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

const NL_TRAIT_NAMES: Record<string, string> = {
  Loyal: 'Loyaal',
  Energetic: 'Energiek',
  Social: 'Sociaal',
  Intelligent: 'Intelligent',
  Vocal: 'Spraakzaam',
  Calm: 'Rustig',
  Gentle: 'Zacht',
  Independent: 'Onafhankelijk',
  Protective: 'Beschermend',
};
const NL_ORIGIN: Record<string, string> = {
  England: 'Engeland',
  'United States': 'Verenigde Staten',
  'United Kingdom': 'Verenigd Koninkrijk',
  Switzerland: 'Zwitserland',
  Ethiopia: 'Ethiopië',
  'Myanmar (Burma)': 'Myanmar (Birma)',
  Scotland: 'Schotland',
  'Thailand (Siam)': 'Thailand (Siam)',
  Canada: 'Canada',
  Russia: 'Rusland',
  'Iran (Persia)': 'Iran (Perzië)',
  'Maine, United States': 'Maine, Verenigde Staten',
  Mediterranean: 'Mediterraan',
  Germany: 'Duitsland',
  Australia: 'Australië',
  France: 'Frankrijk',
  'Border of England and Scotland': 'Grens van Engeland en Schotland',
  'Scotland, United Kingdom': 'Schotland, Verenigd Koninkrijk',
  'Houses with yards, active families': 'Woningen met tuinen, actieve gezinnen',
  'Houses with yards, family homes': 'Woningen met tuinen, gezinswoningen',
  'Active homes with yards': 'Actieve huishoudens met tuinen',
  'Apartments or houses': 'Appartementen of woningen',
  'Family homes with space': 'Gezinswoningen met ruimte',
  'Rural areas, active homes, farms':
    'Landelijk gebied, actieve huishoudens, boerderijen',
  'Apartments, urban settings, moderate climate':
    'Appartementen, stedelijke omgeving, gematigd klimaat',
  'Active homes with vertical space':
    'Actieve huishoudens met verticale ruimte',
  'Active homes with space': 'Actieve huishoudens met ruimte',
  'Indoor homes, family settings': 'Binnenhuizen, gezinsomgeving',
  'Indoor homes, quiet environments': 'Binnenhuizen, rustige omgeving',
  'Indoor homes, active households': 'Binnenhuizen, actieve huishoudens',
  'Indoor homes, quiet settings': 'Binnenhuizen, rustige omgeving',
  'Spacious homes, indoor/outdoor access':
    'Ruime woningen, binnen-/buiten toegang',
  'Indoor homes, warm environments': 'Binnenhuizen, warme omgeving',
  'Apartments, any home': 'Appartementen, elk huis',
  'Houses with yards, cooler climates': 'Woningen met tuinen, koelere klimaten',
  Families: 'Gezinnen',
  'Cooler climates': 'Koelere klimaten',
};
const NL_BEST_FOR: Record<string, string> = {
  'Active families': 'Actieve gezinnen',
  'Those with yards': 'Mensen met een tuin',
  'First-time owners': 'Eerste keer eigenaar',
  Families: 'Gezinnen',
  'Experienced cat owners': 'Ervaren katteneigenaren',
  'Those wanting playful cats': 'Mensen die een speelse kat willen',
  'Very active owners': 'Zeer actieve eigenaren',
  'Those wanting unique cats': 'Mensen die een unieke kat willen',
  'Experienced owners': 'Ervaren eigenaren',
  'First-time cat owners': 'Eerste keer katteneigenaar',
  Seniors: 'Ouderen',
  'Those wanting affectionate cats': 'Mensen die een aanhankelijke kat willen',
  'Quiet households': 'Rustige huishoudens',
  'Busy professionals': 'Drukke professionals',
  'Active households': 'Actieve huishoudens',
  Hunters: 'Jagers',
  'Very active individuals': 'Zeer actieve personen',
  Farmers: 'Boeren',
  'Families with children': 'Gezinnen met kinderen',
  'Apartment dwellers': 'Appartementsbewoners',
  'Active individuals': 'Actieve personen',
  'Very active families': 'Zeer actieve gezinnen',
  'Those with allergies': 'Mensen met allergieën',
  'Active seniors': 'Actieve senioren',
};
const NL_CARE: Record<string, string> = {
  '1-hour-daily-of-exercise-and-play': '1 uur per dag beweging en spel',
  'weekly-brushing-regular-baths': 'Wekelijks borstelen, regelmatig baden',
  'yard-preferred-secure-fencing-essential':
    'Bij voorkeur een tuin, veilige omheining noodzakelijk',
  'moderate-to-high-needs-exercise-and-training':
    'Gemiddeld tot hoog — behoefte aan beweging en training',
  'moderate-daily-walks-and-play': 'Gemiddeld — dagelijkse wandelingen en spel',
  'regular-brushing-heavy-shedding': 'Regelmatig borstelen, veel verharing',
  'large-yard-preferred': 'Grote tuin bij voorkeur',
  'minimal-weekly-brushing': 'Minimaal — wekelijks borstelen',
  'vertical-space-for-climbing-essential':
    'Verticale ruimte om te klimmen noodzakelijk',
  'moderate-needs-interaction': 'Gemiddeld — behoefte aan interactie',
  'very-high-needs-constant-stimulation':
    'Zeer hoog — behoefte aan constante stimulatie',
  'needs-space-to-run-and-climb': 'Heeft ruimte nodig om te rennen en klimmen',
  'high-very-interactive': 'Hoog — zeer interactief',
  'moderate-enjoys-play-sessions': 'Gemiddeld — geniet van speelmomenten',
  'weekly-brushing': 'Wekelijks borstelen',
  'adaptable-to-various-spaces': 'Aanpasbaar aan verschillende ruimtes',
  'moderate-enjoys-companionship': 'Gemiddeld — geniet van gezelschap',
  'low-to-moderate': 'Laag tot gemiddeld',
  'adaptable-to-small-spaces': 'Aanpasbaar aan kleine ruimtes',
  'low-very-independent': 'Laag — zeer onafhankelijk',
  'moderate-enjoys-play': 'Gemiddeld — geniet van spel',
  adaptable: 'Aanpasbaar',
  'moderate-loves-attention': 'Gemiddeld — houdt van aandacht',
  'high-very-active': 'Hoog — zeer actief',
  'minimal-gentle-weekly-grooming':
    'Minimaal — voorzichtig wekelijks verzorgen',
  'needs-climbing-spaces': 'Heeft klimruimte nodig',
  'moderate-to-high-very-interactive': 'Gemiddeld tot hoog — zeer interactief',
  'every-6-months': 'Elke 6 maanden',
};
const NL_HEALTH: Record<string, string> = {
  obesity: 'Obesitas',
  epilepsy: 'Epilepsie',
  'hip-dysplasia': 'Heupdysplasie',
  'eye-problems': 'Oogproblemen',
  'weight-management': 'Gewichtsbeheer',
  'regular-exercise': 'Regelmatige beweging',
  'eye-exams': 'Oogonderzoek',
  'proper-diet': 'Gebalanceerd dieet',
  'regular-vet-visits': 'Regelmatige dierenartsbezoeken',
  'joint-care': 'Gewrichtsverzorging',
  'dental-care': 'Gebitsverzorging',
  'regular-checkups': 'Regelmatige controles',
  'heart-disease': 'Hartaandoeningen',
  'ear-infections': 'Oorinfecties',
  'skin-issues': 'Huidproblemen',
  'dental-problems': 'Gebitsproblemen',
  'skin-care': 'Huidverzorging',
  'dental-hygiene': 'Mondhygiëne',
  'kidney-disease': 'Nieraandoeningen',
  diabetes: 'Diabetes',
  bloat: 'Maagkanteling',
  cancer: 'Kanker',
  'elbow-dysplasia': 'Elleboogdysplasie',
  'dental-issues': 'Gebitsproblemen',
};
const NL_FREQUENCY: Record<string, string> = {
  annually: 'Jaarlijks',
  'every-6-months': 'Elke 6 maanden',
};
const NL_LIFESPAN: Record<string, string> = {
  '13-16-years': '13-16 jaar',
  '12-15-years': '12-15 jaar',
  '12-16-years': '12-16 jaar',
  '12-17-years': '12-17 jaar',
  '9-15-years': '9-15 jaar',
  '7-10-years': '7-10 jaar',
  '15-20-years': '15-20 jaar',
  '11-14-years': '11-14 jaar',
  '8-14-years': '8-14 jaar',
  '10-12-years': '10-12 jaar',
  '10-13-years': '10-13 jaar',
  '10-15-years': '10-15 jaar',
  '12-14-years': '12-14 jaar',
  '14-16-years': '14-16 jaar',
};
const NL_YEARS = 'jaar';

function getDutchDynamic(
  category:
    | 'trait'
    | 'origin'
    | 'bestFor'
    | 'care'
    | 'health'
    | 'frequency'
    | 'lifespan'
    | 'years',
  rawValue: string
): string | undefined {
  switch (category) {
    case 'trait':
      return NL_TRAIT_NAMES[rawValue];
    case 'origin':
      return NL_ORIGIN[rawValue];
    case 'bestFor':
      return NL_BEST_FOR[rawValue];
    case 'care':
      return NL_CARE[slugForValue(rawValue)];
    case 'health':
      return NL_HEALTH[slugForValue(rawValue)];
    case 'frequency':
      return NL_FREQUENCY[slugForValue(rawValue)];
    case 'lifespan':
      return NL_LIFESPAN[slugForValue(rawValue)];
    case 'years':
      return NL_YEARS;
    default:
      return undefined;
  }
}

function getBreedDescriptionForRoute(
  lang: FindYourPetLanguage,
  breedId: string,
  fallback: string
): string {
  if (lang === 'en') return fallback;
  if (lang === 'nl') {
    const id = breedId.trim().toLowerCase().replace(/\s+/g, '-');
    const nl = NL_BREED_DESCRIPTIONS[id];
    if (nl) return nl;
  }
  return getFindYourPetTranslation(
    `breedDescriptions.${breedId}`,
    lang,
    fallback
  );
}

export const Route = createFileRoute('/dashboard/know-your-pet')({
  component: KnowYourPetRoute,
});

function normalizeKey(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/['".,()/\\]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

function findBreed(
  breedInput: string | undefined,
  species: string | undefined
) {
  if (!breedInput) return null;

  const normalizedInput = normalizeKey(breedInput);
  const normalizedSpeciesRaw = species ? normalizeKey(species) : undefined;
  // The API currently stores non-dog/cat/bird/reptile pets as `species: "other"`.
  // If we have "other", don't restrict matching by petType.
  const speciesFilter =
    normalizedSpeciesRaw && normalizedSpeciesRaw !== 'other'
      ? normalizedSpeciesRaw
      : undefined;

  // 1) Exact ID match (preferred)
  const byId = findYourPetBreeds.find(
    (b) =>
      b.id === normalizedInput &&
      (!speciesFilter || normalizeKey(b.petType) === speciesFilter)
  );
  if (byId) return byId;

  // 2) Exact name match (case/space-insensitive)
  const byName = findYourPetBreeds.find(
    (b) =>
      normalizeKey(b.name) === normalizedInput &&
      (!speciesFilter || normalizeKey(b.petType) === speciesFilter)
  );
  if (byName) return byName;

  // 3) Prefix/contains match (Persian -> Persian Cat)
  const byPrefix = findYourPetBreeds.find(
    (b) =>
      normalizeKey(b.name).startsWith(normalizedInput) &&
      (!speciesFilter || normalizeKey(b.petType) === speciesFilter)
  );
  if (byPrefix) return byPrefix;

  const byContains = findYourPetBreeds.find(
    (b) =>
      normalizeKey(b.name).includes(normalizedInput) &&
      (!speciesFilter || normalizeKey(b.petType) === speciesFilter)
  );
  if (byContains) return byContains;

  // 4) ID match without species filter (still useful if species is missing/mismatched)
  return findYourPetBreeds.find((b) => b.id === normalizedInput) ?? null;
}

function energyToScore(level: FindYourPetBreed['energyLevel']) {
  switch (level) {
    case 'low':
      return 2;
    case 'medium':
      return 3;
    case 'high':
      return 5;
  }
}

function normalizeFindYourPetLanguage(language: string): FindYourPetLanguage {
  const code = (language || '').toLowerCase().split('-')[0];
  if (code === 'fr') return 'fr';
  if (code === 'es') return 'es';
  if (code === 'pl') return 'pl';
  if (code === 'nl') return 'nl';
  if (code === 'de') return 'de';
  if (code === 'it') return 'it';
  return 'en';
}

function translateYears(lifespan: string, yearsWord: string) {
  return lifespan.replace(/years/i, yearsWord);
}

function groomingToScore(groomingText: string) {
  const v = groomingText.toLowerCase();
  if (v.includes('minimal') || v.includes('low')) return 2;
  if (v.includes('weekly') || v.includes('regular')) return 3;
  if (v.includes('daily') || v.includes('frequent')) return 4;
  return 3;
}

function healthSensitivityToScore(commonRisks: string[]) {
  if (commonRisks.length <= 2) return 2;
  if (commonRisks.length <= 4) return 3;
  return 4;
}

function Indicator({
  label,
  score,
  max = 5,
}: {
  label: string;
  score: number;
  max?: number;
}) {
  const value = Math.round((score / max) * 100);
  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="text-sm text-muted-foreground truncate">{label}</span>
        <span className="text-sm font-medium text-primary shrink-0">
          {score}/{max}
        </span>
      </div>
      <Progress value={value} className="h-2 min-w-0" />
    </div>
  );
}

function KnowYourPetRoute() {
  const { t, i18n } = useTranslation();
  const { selectedPet, isLoading } = usePets();
  const findYourPetLanguage = normalizeFindYourPetLanguage(
    i18n.resolvedLanguage || i18n.language
  );
  const [petPhotoUrl, setPetPhotoUrl] = useState<string | undefined>(undefined);

  // Keep pet photo in sync with profile photo behavior:
  // - Prefer signed URL (via photoKey)
  // - Fallback to stored imageUrl
  // - Fallback to placeholder if neither exists
  useEffect(() => {
    let isMounted = true;

    const loadPetPhoto = async () => {
      if (!selectedPet) {
        setPetPhotoUrl(undefined);
        return;
      }

      // Show any existing public URL immediately while we try a signed URL.
      setPetPhotoUrl(selectedPet.imageUrl || undefined);

      if (!selectedPet.photoKey) return;
      try {
        const { url } = await getSignedUrl(selectedPet.photoKey, 'inline');
        if (isMounted) setPetPhotoUrl(url);
      } catch (err) {
        // If signed URL fetch fails, keep the imageUrl fallback (if any).
        console.error('Failed to fetch signed pet photo URL:', err);
      }
    };

    loadPetPhoto();
    return () => {
      isMounted = false;
    };
  }, [selectedPet?.id, selectedPet?.photoKey, selectedPet?.imageUrl]);

  const resolved = useMemo(() => {
    if (!selectedPet) return null;
    const breed = findBreed(selectedPet.breed, selectedPet.species);
    const petType = findYourPetPetTypes.find(
      (p) => p.id === (breed?.petType ?? selectedPet.species)
    );
    return { breed, petType };
  }, [selectedPet]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!selectedPet) {
    return <NoPetsEmptyState />;
  }

  const petTypeLabel = t(`pet.${selectedPet.species}`) || selectedPet.species;
  const genericSpeciesKey =
    selectedPet.species === 'dog' || selectedPet.species === 'cat'
      ? selectedPet.species
      : 'other';

  if (!resolved?.breed) {
    const petTypeFallbackUrl = getPetTypeFallbackUrl(
      selectedPet.species,
      1100,
      620
    );
    const heroImageUrl = petPhotoUrl ?? petTypeFallbackUrl;
    return (
      <div className="space-y-6 px-3 sm:px-4 min-w-0">
        <Card className="overflow-hidden min-w-0">
          <div className="flex flex-col sm:flex-row items-stretch">
            <div className="bg-muted w-full sm:basis-2/5 flex-none">
              <div className="h-48 sm:h-60 md:h-[260px] overflow-hidden flex items-center justify-center">
                <ImageWithFallback
                  src={heroImageUrl}
                  fallbackSrc={petTypeFallbackUrl}
                  alt={t('knowYourPet.petImageAlt', {
                    petName: selectedPet.name,
                  })}
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>

            <div className="w-full sm:basis-3/5 flex-none p-4 sm:p-6 min-w-0">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="text-xl sm:text-2xl font-semibold text-foreground">
                    {t('knowYourPet.title')}
                  </div>
                  <div className="text-sm text-muted-foreground break-words">
                    {t('knowYourPet.generalCareLabel', {
                      petType: petTypeLabel,
                    })}
                  </div>
                </div>
                <Badge variant="secondary" className="whitespace-nowrap">
                  {t('knowYourPet.unrecognizedBadge')}
                </Badge>
              </div>

              <div className="mt-4 text-sm text-muted-foreground break-words">
                {t('knowYourPet.generalNoticeBody')}
              </div>
            </div>
          </div>
        </Card>

        <Card className="border-blue-200/70 dark:border-blue-900/40 min-w-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="w-5 h-5" />
              {t('knowYourPet.generalNoticeTitle')}
            </CardTitle>
            <CardDescription>
              {t('knowYourPet.generalNoticeBody')}
            </CardDescription>
          </CardHeader>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="w-5 h-5" />
                {t('knowYourPet.sections.personality')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {[
                  t(`knowYourPet.generic.${selectedPet.species}.personality.1`),
                  t(`knowYourPet.generic.${selectedPet.species}.personality.2`),
                  t(`knowYourPet.generic.${selectedPet.species}.personality.3`),
                  t(`knowYourPet.generic.${selectedPet.species}.personality.4`),
                ].map((line, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="mt-1 text-primary">•</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card className="border-blue-200/70 dark:border-blue-900/40">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="w-5 h-5" />
                {t('knowYourPet.generalNoticeTitle')}
              </CardTitle>
              <CardDescription>
                {t('knowYourPet.generalNoticeBody')}
              </CardDescription>
            </CardHeader>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="w-5 h-5" />
                  {t('knowYourPet.sections.personality')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {[
                    t(`knowYourPet.generic.${genericSpeciesKey}.personality.1`),
                    t(`knowYourPet.generic.${genericSpeciesKey}.personality.2`),
                    t(`knowYourPet.generic.${genericSpeciesKey}.personality.3`),
                    t(`knowYourPet.generic.${genericSpeciesKey}.personality.4`),
                  ].map((line, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="mt-1 text-primary">•</span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Home className="w-5 h-5" />
                  {t('knowYourPet.sections.lifestyle')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {[
                    t(`knowYourPet.generic.${genericSpeciesKey}.lifestyle.1`),
                    t(`knowYourPet.generic.${genericSpeciesKey}.lifestyle.2`),
                    t(`knowYourPet.generic.${genericSpeciesKey}.lifestyle.3`),
                    t(`knowYourPet.generic.${genericSpeciesKey}.lifestyle.4`),
                  ].map((line, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="mt-1 text-primary">•</span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Stethoscope className="w-5 h-5" />
                  {t('knowYourPet.sections.health')}
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-semibold mb-2">
                    {t('knowYourPet.generic.commonHealth')}
                  </h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {[
                      t(`knowYourPet.generic.${genericSpeciesKey}.health.1`),
                      t(`knowYourPet.generic.${genericSpeciesKey}.health.2`),
                      t(`knowYourPet.generic.${genericSpeciesKey}.health.3`),
                      t(`knowYourPet.generic.${genericSpeciesKey}.health.4`),
                    ].map((line, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="mt-1 text-primary">•</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-sm font-semibold mb-2">
                    {t('knowYourPet.sections.preventativeCare')}
                  </h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {[
                      t(
                        `knowYourPet.generic.${genericSpeciesKey}.preventative.1`
                      ),
                      t(
                        `knowYourPet.generic.${genericSpeciesKey}.preventative.2`
                      ),
                      t(
                        `knowYourPet.generic.${genericSpeciesKey}.preventative.3`
                      ),
                      t(
                        `knowYourPet.generic.${genericSpeciesKey}.preventative.4`
                      ),
                    ].map((line, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="mt-1 text-primary">✓</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  const breed = resolved.breed;
  const breedImageUrl = getFindYourPetImageUrl(
    breed.id,
    breed.image,
    breed.petType,
    1100,
    620
  );
  const heroImageUrl = petPhotoUrl ?? breedImageUrl;
  const petTypeFallbackUrl = getPetTypeFallbackUrl(breed.petType, 1100, 620);

  const isDutch = (i18n.resolvedLanguage || i18n.language || '')
    .toLowerCase()
    .startsWith('nl');
  const breedIdNorm = breed.id.trim().toLowerCase().replace(/\s+/g, '-');
  const breedDescription =
    isDutch && NL_BREED_DESCRIPTIONS[breedIdNorm]
      ? NL_BREED_DESCRIPTIONS[breedIdNorm]
      : getBreedDescriptionForRoute(
          findYourPetLanguage,
          breed.id,
          breed.description
        );
  const resolvedBreedDescription =
    findYourPetLanguage === 'it' && breedDescription === breed.description
      ? t('knowYourPet.breedDescriptionFallback', { breedName: breed.name })
      : breedDescription;

  const yearsWord = isDutch
    ? NL_YEARS
    : getFindYourPetTranslation(
        'yearsTranslations.years',
        findYourPetLanguage,
        'years',
        t
      );
  const lifespanRaw = isDutch
    ? (getDutchDynamic('lifespan', breed.health.lifespan) ??
      breed.health.lifespan)
    : getFindYourPetTranslation(
        `lifespanTranslations.${breed.health.lifespan}`,
        findYourPetLanguage,
        breed.health.lifespan,
        t
      );
  const translatedLifespan = translateYears(lifespanRaw, yearsWord);

  const energyScore = energyToScore(breed.energyLevel);
  const groomingText = isDutch
    ? (getDutchDynamic('care', breed.care.groomingRequirements) ??
      breed.care.groomingRequirements)
    : getFindYourPetTranslation(
        `careTranslations.${breed.care.groomingRequirements}`,
        findYourPetLanguage,
        breed.care.groomingRequirements,
        t
      );
  const groomingScore = groomingToScore(groomingText);
  const healthScore = healthSensitivityToScore(breed.health.commonRisks);

  return (
    <div className="min-w-0 px-3 sm:px-4">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4 sm:mb-6">
        <h1
          className="text-2xl sm:text-3xl text-[#2D2F7E] dark:text-white min-w-0"
          style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
        >
          {t('knowYourPet.title')}
        </h1>
        <DashboardHeaderControls />
      </div>
      <div className="space-y-6">
        <Card className="overflow-hidden min-w-0">
          <div className="flex flex-col sm:flex-row items-stretch">
            <div className="bg-muted w-full sm:basis-2/5 flex-none">
              <div className="h-48 sm:h-60 md:h-[260px] overflow-hidden flex items-center justify-center">
                <ImageWithFallback
                  src={heroImageUrl}
                  fallbackSrc={petPhotoUrl ? breedImageUrl : petTypeFallbackUrl}
                  alt={
                    petPhotoUrl
                      ? t('knowYourPet.petImageAlt', {
                          petName: selectedPet.name,
                        })
                      : t('knowYourPet.imageAlt', { breedName: breed.name })
                  }
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>

            <div className="w-full sm:basis-3/5 flex-none p-4 sm:p-6 min-w-0">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="text-2xl sm:text-3xl font-semibold text-foreground">
                    {breed.name}
                  </div>
                  <div className="text-sm text-muted-foreground break-words">
                    {t('knowYourPet.breedCareLabel', { breedName: breed.name })}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Badge className="capitalize">
                    {t(`knowYourPet.values.size.${breed.size}`)}
                  </Badge>
                  <Badge className="capitalize">
                    {t(`knowYourPet.values.energy.${breed.energyLevel}`)}
                  </Badge>
                  {breed.apartmentFriendly && (
                    <Badge variant="secondary">
                      {t('knowYourPet.badges.apartmentFriendly')}
                    </Badge>
                  )}
                  {breed.familyFriendly && (
                    <Badge variant="secondary">
                      {t('knowYourPet.badges.familyFriendly')}
                    </Badge>
                  )}
                </div>
              </div>

              {/* No "Breed Overview" heading here (kept in the full Overview section below) */}
              <div className="mt-4 sm:mt-6 text-base text-muted-foreground break-words">
                {resolvedBreedDescription}
              </div>

              <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t">
                <div className="text-sm font-semibold">
                  {t('knowYourPet.labels.lifespan')}
                </div>
                <div className="text-sm text-muted-foreground">
                  {translatedLifespan}
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Card className="min-w-0 overflow-hidden">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2">
              <Home className="w-5 h-5 shrink-0" />
              {t('knowYourPet.sections.overview')}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 p-4 sm:p-6 pt-0">
            <div className="space-y-3 min-w-0">
              <div>
                <div className="text-sm font-semibold">
                  {t('knowYourPet.labels.origin')}
                </div>
                <div className="text-sm text-muted-foreground break-words">
                  {isDutch
                    ? (getDutchDynamic('origin', breed.origin) ?? breed.origin)
                    : getFindYourPetTranslation(
                        `originTranslations.${breed.origin}`,
                        findYourPetLanguage,
                        breed.origin,
                        t
                      )}
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold">
                  {t('knowYourPet.labels.idealEnvironment')}
                </div>
                <div className="text-sm text-muted-foreground break-words">
                  {isDutch
                    ? (getDutchDynamic('origin', breed.idealEnvironment) ??
                      breed.idealEnvironment)
                    : getFindYourPetTranslation(
                        `originTranslations.${breed.idealEnvironment}`,
                        findYourPetLanguage,
                        breed.idealEnvironment,
                        t
                      )}
                </div>
              </div>
            </div>
            <div className="space-y-3 min-w-0">
              <div>
                <div className="text-sm font-semibold">
                  {t('knowYourPet.labels.idealOwner')}
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {breed.bestFor.map((v) => (
                    <Badge key={v} variant="outline">
                      {isDutch
                        ? (getDutchDynamic('bestFor', v) ?? v)
                        : getFindYourPetTranslation(
                            `bestForTranslations.${v}`,
                            findYourPetLanguage,
                            v,
                            t
                          )}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="min-w-0 overflow-hidden">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 shrink-0" />
              {t('knowYourPet.sections.personality')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5 p-4 sm:p-6 pt-0">
            {breed.traits.map((trait) => (
              <div key={trait.name} className="min-w-0">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <span className="text-sm text-muted-foreground flex items-center gap-2 min-w-0">
                    <span aria-hidden="true" className="shrink-0">
                      {trait.icon}
                    </span>
                    <span className="truncate">
                      {isDutch
                        ? (getDutchDynamic('trait', trait.name) ?? trait.name)
                        : getFindYourPetTranslation(
                            `traitNames.${trait.name}`,
                            findYourPetLanguage,
                            trait.name,
                            t
                          )}
                    </span>
                  </span>
                  <span className="text-sm font-medium text-primary shrink-0">
                    {trait.level}/5
                  </span>
                </div>
                <Progress value={trait.level * 20} className="h-2 min-w-0" />
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="min-w-0 overflow-hidden">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 shrink-0" />
                {t('knowYourPet.sections.lifestyle')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground p-4 sm:p-6 pt-0 break-words">
              <div>
                <div className="font-semibold text-foreground">
                  {t('knowYourPet.labels.exerciseNeeds')}
                </div>
                <div>
                  {isDutch
                    ? (getDutchDynamic('care', breed.care.exerciseNeeds) ??
                      breed.care.exerciseNeeds)
                    : getFindYourPetTranslation(
                        `careTranslations.${breed.care.exerciseNeeds}`,
                        findYourPetLanguage,
                        breed.care.exerciseNeeds,
                        t
                      )}
                </div>
              </div>
              <div>
                <div className="font-semibold text-foreground">
                  {t('knowYourPet.labels.spaceRequirements')}
                </div>
                <div>
                  {isDutch
                    ? (getDutchDynamic('care', breed.care.spaceRequirements) ??
                      breed.care.spaceRequirements)
                    : getFindYourPetTranslation(
                        `careTranslations.${breed.care.spaceRequirements}`,
                        findYourPetLanguage,
                        breed.care.spaceRequirements,
                        t
                      )}
                </div>
              </div>
              <div>
                <div className="font-semibold text-foreground">
                  {t('knowYourPet.labels.groomingNeeds')}
                </div>
                <div>{groomingText}</div>
              </div>
              <div>
                <div className="font-semibold text-foreground">
                  {t('knowYourPet.labels.timeCommitment')}
                </div>
                <div>
                  {isDutch
                    ? (getDutchDynamic('care', breed.care.timeCommitment) ??
                      breed.care.timeCommitment)
                    : getFindYourPetTranslation(
                        `careTranslations.${breed.care.timeCommitment}`,
                        findYourPetLanguage,
                        breed.care.timeCommitment,
                        t
                      )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="min-w-0 overflow-hidden">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2">
                <ShieldPlus className="w-5 h-5 shrink-0" />
                {t('knowYourPet.sections.indicators')}
              </CardTitle>
              <CardDescription className="break-words">
                {t('knowYourPet.indicatorsHelp')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5 p-4 sm:p-6 pt-0">
              <Indicator
                label={t('knowYourPet.indicators.energy')}
                score={energyScore}
              />
              <Indicator
                label={t('knowYourPet.indicators.grooming')}
                score={groomingScore}
              />
              <Indicator
                label={t('knowYourPet.indicators.healthSensitivity')}
                score={healthScore}
              />
            </CardContent>
          </Card>
        </div>

        <Card className="min-w-0 overflow-hidden border-red-200/70 dark:border-red-900/40">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2">
              <Stethoscope className="w-5 h-5 shrink-0" />
              {t('knowYourPet.sections.health')}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 p-4 sm:p-6 pt-0">
            <div className="min-w-0">
              <div className="text-sm font-semibold mb-2">
                {t('knowYourPet.labels.commonHealthRisks')}
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {breed.health.commonRisks.map((risk) => (
                  <li key={risk} className="flex items-start gap-2 min-w-0">
                    <span className="mt-1 text-red-500 shrink-0">•</span>
                    <span className="break-words min-w-0">
                      {isDutch
                        ? (getDutchDynamic('health', risk) ?? risk)
                        : getFindYourPetTranslation(
                            `healthTranslations.${risk}`,
                            findYourPetLanguage,
                            risk,
                            t
                          )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="min-w-0">
              <div className="text-sm font-semibold mb-2">
                {t('knowYourPet.sections.preventativeCare')}
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {breed.health.preventativeCare.map((tip) => (
                  <li key={tip} className="flex items-start gap-2 min-w-0">
                    <span className="mt-1 text-green-600 shrink-0">✓</span>
                    <span className="break-words min-w-0">
                      {isDutch
                        ? (getDutchDynamic('health', tip) ?? tip)
                        : getFindYourPetTranslation(
                            `healthTranslations.${tip}`,
                            findYourPetLanguage,
                            tip,
                            t
                          )}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-5 pt-4 border-t grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="min-w-0">
                  <div className="text-sm font-semibold">
                    {t('knowYourPet.labels.vetCheckups')}
                  </div>
                  <div className="text-sm text-muted-foreground break-words">
                    {isDutch
                      ? (getDutchDynamic(
                          'frequency',
                          breed.health.vetFrequency
                        ) ?? breed.health.vetFrequency)
                      : getFindYourPetTranslation(
                          `frequencyTranslations.${breed.health.vetFrequency}`,
                          findYourPetLanguage,
                          breed.health.vetFrequency,
                          t
                        )}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold">
                    {t('knowYourPet.labels.lifespan')}
                  </div>
                  <div className="text-sm text-muted-foreground break-words">
                    {translatedLifespan}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="min-w-0 overflow-hidden border-blue-200/70 dark:border-blue-900/40">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2">
              <Info className="w-5 h-5 shrink-0" />
              {t('knowYourPet.sections.careTips')}
            </CardTitle>
            <CardDescription className="break-words">
              {t('knowYourPet.careTipsDisclaimer')}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 p-4 sm:p-6 pt-0">
            <div className="min-w-0">
              <div className="text-sm font-semibold mb-2">
                {t('knowYourPet.careTips.bestPractices')}
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {[1, 2, 3, 4].map((n) => (
                  <li key={n} className="flex items-start gap-2 min-w-0">
                    <span className="mt-1 text-primary shrink-0">✓</span>
                    <span className="break-words min-w-0">
                      {t(`knowYourPet.careTips.practice.${n}`)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold mb-2">
                {t('knowYourPet.careTips.mistakesToAvoid')}
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {[1, 2, 3, 4].map((n) => (
                  <li key={n} className="flex items-start gap-2 min-w-0">
                    <span className="mt-1 text-primary shrink-0">•</span>
                    <span className="break-words min-w-0">
                      {t(`knowYourPet.careTips.mistake.${n}`)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card className="min-w-0 overflow-hidden">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2">
              <ShieldPlus className="w-5 h-5 shrink-0" />
              {t('knowYourPet.sections.indicators')}
            </CardTitle>
            <CardDescription className="break-words">
              {t('knowYourPet.indicatorsHelp')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5 p-4 sm:p-6 pt-0">
            <Indicator
              label={t('knowYourPet.indicators.energy')}
              score={energyScore}
            />
            <Indicator
              label={t('knowYourPet.indicators.grooming')}
              score={groomingScore}
            />
            <Indicator
              label={t('knowYourPet.indicators.healthSensitivity')}
              score={healthScore}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
